
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { getPictureInPictureModeConfig } from '../../../../base/config/functions.any';
import { setMarqueeClosed } from '../../../actions';
import { checkRegistrationStatus } from '../utility/registrationUtils';
import { removedecimal } from '../utility/util';

import StreamerList from './StreamerList';


const Streamer = props => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [ progress, setProgress ] = useState(0);
    const [ streamers, setStreamers ] = useState([]);
    const [ streamerCount, setStreamerCount ] = useState(5);
    const [ totalRaised, setTotalRaised ] = useState(0);
    const [ isAlreadyRegistered, setIsAlreadyRegistered ] = useState(false);
    const isMarqueeClosed = useSelector(state => state['features/clickandpledge']?.isMarqueeClosed || false);
    const streamoptions = useSelector(state => state['features/clickandpledge']?.streamOptions || {});
    const campRaisedSettings = useSelector(state => state['features/clickandpledge']?.campaignRaisedDetails || {});
    const noRecurringOptionAvailable = useSelector(state => state['features/clickandpledge']?.noRecurringOptionAvailable || false);
    const formHeader = useSelector(state => state['features/clickandpledge']?.formSettings?.FormHeader || '');
    const isPictureInPictureEnabled = useSelector(getPictureInPictureModeConfig);
    const bRef = useRef(null);
    const progressBarRef = useRef(null);
    const logostyle = { display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '12px',
        width: '100%' };

    // Check registration status on component mount
    useEffect(() => {
        const checkRegistration = async () => {
            if (props.meetingData && props.userData) {
                try {
                    const registrationStatus = await checkRegistrationStatus(props.meetingData, props.userData);

                    setIsAlreadyRegistered(registrationStatus.isAlreadyRegistered);
                } catch (error) {
                    setIsAlreadyRegistered(false);
                }
            }
        };

        checkRegistration();
    }, [ props.meetingData, props.userData ]);

    useEffect(() => {
        const data = [ ...props.data ].reverse();

        setStreamers(data.map((obj, index) => {
            return {
                ...obj,
                id: index
            };
        }));

        const raised = campRaisedSettings?.CampaignRaised || 0;

        setTotalRaised(parseFloat(removedecimal(raised)));
        const goalAmt = campRaisedSettings?.CampaignTarget || 0;
        const progrs = goalAmt > 0 ? (removedecimal(raised) / removedecimal(goalAmt)) * 100 : 0;

        setProgress(progrs);
    }, [ props.data, campRaisedSettings ]);

    // Update progress bar width when progress changes
    useEffect(() => {
        if (progressBarRef.current) {
            const rawPct = isNaN(progress) ? 0 : parseFloat(progress);
            const widthPct = Math.min(100, Math.max(0, rawPct));

            progressBarRef.current.style.width = `${widthPct}%`;
        }
    }, [ progress ]);

    const handleDonateClick = () => {
        if (isAlreadyRegistered) {
            // For already registered users, show form but skip to payment section
            sessionStorage.setItem('skipToPayment', 'true');
        }
        props.handleShowForm();
    };

    const raiseUp = amt => {
        const cleanAmt = amt ? parseFloat(String(amt).replace(/[^0-9.]/g, '')) : amt;

        // Store the boost amount for the form
        sessionStorage.setItem('seedAmount', cleanAmt);
        sessionStorage.setItem('boostClicked', 'true');

        // Notify parent that boost was clicked and form should be shown
        if (props.boostUpDonation) {
            props.boostUpDonation(cleanAmt);
        } else if (props.handleShowForm) {
            props.handleShowForm(cleanAmt);
        }
    };

    useEffect(() => {
        const updateMaxHeight = () => {
            const donate = props.fRef?.current;
            const footer = bRef.current;

            if (!donate || !footer) {
                return;
            }

            const div1Top = donate.getBoundingClientRect().top;
            const div2Top = footer.getBoundingClientRect().top;
            const heightBetweenDivs = Math.abs(div2Top - div1Top);
            const diffbetweenDivs = parseInt(heightBetweenDivs, 10) - 75;
            const streamCount = parseInt(diffbetweenDivs / 40, 10);

            setStreamerCount(streamCount);
        };

        updateMaxHeight();
        window.addEventListener('resize', updateMaxHeight);
        window.scrollTo(0, 0);

        return () => {
            window.removeEventListener('resize', updateMaxHeight);
        };
    }, [ props.fRef ]);

    const handleShowMarquee = () => {
        dispatch(setMarqueeClosed(false));
    };

    return (
        <div className = 'transaction_info_wrapper screen_1_section streamer_section'>
            {!props.hideHeader && formHeader && !noRecurringOptionAvailable
                && <div
                    className = 'transaction_info_logo_container'
                    style = { logostyle }>
                    <div className = 'header-name'>{formHeader}</div>
                </div>
            }
            {isMarqueeClosed && streamers.length > 0 && streamoptions.SPS === 1 && !isPictureInPictureEnabled && (
                <button
                    className = 'section_btn streamer_show_marquee_btn'
                    onClick = { handleShowMarquee }
                    type = 'button'>
                    {t('clickandpledge.showMarquee')}
                </button>
            )}
            <div
                className = 'recurring_amount_info_wrapper full-width'>
                <div className = 'form_input_wrapper'>
                    <div className = 'progress_section_wrapper'>
                        {streamoptions.SPM === 1 && (
                            <>
                                {campRaisedSettings.CampaignTarget > 0 ? (
                                    <>
                                        <div className = 'progress_info_wrapper'>
                                            <div className = 'raised'>
                                                {props.currency}
                                                {campRaisedSettings.CampaignRaised > 0
                                                    ? new Intl.NumberFormat('en-US', { minimumFractionDigits: 2 }).format(totalRaised)
                                                    : '0.00'}
                                            </div>
                                            <div className = 'goal'>
                                                {t('clickandpledge.goal')} {props.currency + new Intl.NumberFormat('en-US', { minimumFractionDigits: 2 })
                                                    .format(removedecimal(campRaisedSettings.CampaignTarget))
                                                    .replace(/\.00$/, '')}
                                            </div>
                                        </div>
                                        <div
                                            className = 'progress-bar-container'
                                            data-progress = { Math.min(100, Math.max(0, isNaN(progress) ? 0 : parseFloat(progress))) }>
                                            <div
                                                className = 'progress-bar'
                                                ref = { progressBarRef } />
                                            <span className = 'progress-text'>
                                                {(isNaN(progress) ? 0 : parseFloat(progress)) > 100
                                                    ? '100% +'
                                                    : `${Math.round(isNaN(progress) ? 0 : parseFloat(progress))}%`}
                                            </span>
                                        </div>
                                    </>
                                ) : (
                                    <div className = 'progress_info_wrapper'>
                                        {campRaisedSettings.CampaignRaised > 0
                                            ? <div className = 'raised'>
                                                {t('clickandpledge.totalRaised')} {props.currency}
                                                {new Intl.NumberFormat('en-US', { minimumFractionDigits: 2 }).format(totalRaised)}
                                            </div>
                                            : (
                                                <div className = 'loading-container'>
                                                    <span className = 'loading-text'>{t('clickandpledge.loadingPleaseWait')}</span>
                                                </div>
                                            )}
                                    </div>
                                )}
                            </>
                        )}
                        <div
                            className = 'form_input_wrapper mt_1'
                            id = 'donate-to-form'
                            ref = { bRef }>
                            <button
                                className = 'full_button tabindex_btn donate_btn'
                                onClick = { handleDonateClick }
                                type = 'button'>{t('clickandpledge.donate')}</button>
                        </div>
                        <div className = 'streamer_wrapper'>
                            {streamers.length > 0 && streamoptions.SPS === 1
                                && <StreamerList
                                    currency = { props.currency }
                                    feedLength = { parseInt(props.data.length / 2, 10) || 1 }
                                    raiseUpDonation = { raiseUp }
                                    streamSizeCount = { streamerCount }
                                    streamerFeed = { streamers } />
                            }
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Streamer;
